/**
 * Production adapter for `send_structure_set_to_pacs`: binds the
 * React-free ports to the same pieces the Export dialog uses — the
 * connector's outbound targets (`fetchListTargets`, Out or InOut), the
 * active structure set's read model, and `useDicomExport().handleExportBatch`
 * (`POST /api/dicom/{target}`), which also marks the set as sent on success.
 * Like the other ops' engineDeps, this browser glue is covered by the manual
 * demo; the logic it feeds is unit-tested through fakes.
 */
import { useCallback } from 'react';
import { useRecoilValue } from 'recoil';
import { fetchListTargets } from '~/backend/connector-api/connector-components';
import { useSessionOrNull } from '~/contexts/SessionContext';
import { DICOMApprovalStatus } from '~/domain/dicom/dicomConstants';
import { useDicomExport } from '~/hooks/export/useDicomExport';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import AppConfig from '~/utils/appconfig';
import type { SendStructureSetDeps } from './toolRunner';

export function useSendStructureSetDeps(): () => Omit<
    SendStructureSetDeps,
    'pendingMutationExists'
> {
    const structureSet = useRecoilValue(currentStructureSetState);
    const session = useSessionOrNull();
    const { handleExportBatch } = useDicomExport();

    return useCallback(
        () => ({
            connectorMode: AppConfig.isConnectorMode,
            structureSet: () =>
                structureSet
                    ? {
                          uid: structureSet.dicomUid,
                          label: structureSet.structureSetLabel,
                          rois: structureSet.roiArray.map((r) => ({
                              roiNumber: r.roiNumber,
                              name: r.name,
                              type: r.interpretedType,
                              isApproved: r.isApproved,
                              isRejected:
                                  r.approvalStatus ===
                                  DICOMApprovalStatus.REJECTED,
                          })),
                      }
                    : undefined,
            outboundTargets: async () =>
                Object.entries((await fetchListTargets({})) ?? {})
                    .filter(([, dir]) => dir === 'Out' || dir === 'InOut')
                    .map(([name]) => name),
            // Read live from the session: the read model is a snapshot.
            isMarkedSent: (uid) =>
                session?.patient.getStructureSetWithSopInstanceUid(uid)
                    ?.isExported,
            send: (uid, roiNumbers, targetName) =>
                handleExportBatch(
                    [],
                    [{ structureSetUid: uid, selectedRoiNumbers: roiNumbers }],
                    [],
                    targetName,
                    []
                ),
        }),
        [structureSet, session, handleExportBatch]
    );
}
